import * as React from "react";

// MUI
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { grey } from "@mui/material/colors";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

// Third Party
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Player } from "@lottiefiles/react-lottie-player";

// Internal
import SharedAppBar from "../../components/SharedAppBar";
import { GET_COLLECTION } from "../../data/graphql/queries";
import {
  CollectionType,
  CollectionItemType,
  PublicationType,
  PostType,
  TagType,
} from "../../data/types";
import PublicationCard from "../../components/PublicationCard";
import en from "../../data/en.json";
import Loading from "../../assets/animations/loading.json";
import withRoot from "../../withRoot";

function Main() {
  let { uuid } = useParams();

  const [tab, setTab] = React.useState<string>("1");
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  const { loading, error, data } = useQuery(GET_COLLECTION, {
    variables: { uuid: uuid },
  });

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <Player
          autoplay={true}
          loop={true}
          src={Loading}
          style={{ height: "300px", width: "300px" }}
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <Typography variant="h6" color="error">
          {en.common.error}: {error.message}
        </Typography>
      </Box>
    );
  }

  const collection: CollectionType = data.collection;

  if (!collection) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <Typography variant="h6" color={grey[500]}>
          Collection not found
        </Typography>
      </Box>
    );
  }

  const renderPublicationItem = (publication: PublicationType) => (
    <Grid item xs={12} md={6} lg={4} key={publication.uuid}>
      <Link
        to={`/publications/${publication.uuid}`}
        style={{ textDecoration: "none", color: "black" }}
      >
        <Paper
          sx={{
            p: 2,
            height: "auto",
            minHeight: 200,
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "action.hover",
              transform: "translateY(-2px)",
              boxShadow: 3,
            },
          }}
        >
          <PublicationCard publication={publication} hideCoverMedia={true} />
        </Paper>
      </Link>
    </Grid>
  );

  const renderPostItem = (post: PostType) => (
    <Grid item xs={12} md={6} lg={4} key={post.id}>
      <Paper
        sx={{
          p: 2,
          minHeight: 150,
          cursor: "pointer",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            backgroundColor: "action.hover",
            transform: "translateY(-2px)",
            boxShadow: 3,
          },
        }}
      >
        <Box display="flex" flexDirection="column" height="100%">
          <Box display="flex" flexDirection="row" alignItems="center" mb={1}>
            {post.user.profile?.photo?.url ? (
              <Avatar
                sx={{ width: 24, height: 24, mr: 1 }}
                alt="userProfile"
                src={post.user.profile.photo.url}
              />
            ) : (
              <Avatar
                sx={{ bgcolor: "#2B2D6A", width: 24, height: 24, mr: 1 }}
              >
                <AccountCircleIcon fontSize="small" />
              </Avatar>
            )}
            <Typography variant="caption" color="text.secondary">
              @{post.user.handle}
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 4,
              WebkitBoxOrient: "vertical",
              mb: 1,
              flexGrow: 1,
            }}
          >
            {post.text}
          </Typography>

          {post.imageUrl && (
            <Box
              component="img"
              src={post.imageUrl}
              alt="Post image"
              sx={{
                width: "100%",
                height: 100,
                objectFit: "cover",
                borderRadius: 1,
                mt: 1,
              }}
            />
          )}
        </Box>
      </Paper>
    </Grid>
  );

  const renderPublications = (collectionItems: CollectionItemType[]) => {
    const publicationItems = collectionItems.filter(
      (item) => (item.item as any).__typename === "Publication"
    );

    if (publicationItems.length === 0) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <Typography variant="h6" color={grey[400]}>
            No publications in this collection
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        {publicationItems.map((collectionItem: CollectionItemType) => {
          const publication = collectionItem.item as PublicationType;
          return renderPublicationItem(publication);
        })}
      </Grid>
    );
  };

  const renderPosts = (collectionItems: CollectionItemType[]) => {
    const postItems = collectionItems.filter(
      (item) => (item.item as any).__typename === "Post"
    );

    if (postItems.length === 0) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <Typography variant="h6" color={grey[400]}>
            No posts in this collection
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        {postItems.map((collectionItem: CollectionItemType) => {
          const post = collectionItem.item as PostType;
          return renderPostItem(post);
        })}
      </Grid>
    );
  };

  // const sortedItems =
  //   collection.collectionItems?.sort(
  //     (a: CollectionItemType, b: CollectionItemType) =>
  //       (a.position || 0) - (b.position || 0)
  //   ) || [];
  const sortedItems: CollectionItemType[] = collection.collectionItems || [];

  return (
    <React.Fragment>
      <SharedAppBar returnUrl="/" />
      <Box sx={{ maxWidth: 1200, mx: "auto", p: 3, mt: 10 }}>
        {/* Collection Header */}
        <Box sx={{ mb: 4 }}>
          <Box display="flex" alignItems="center" mb={2}>
            {collection.user.profile?.photo?.url ? (
              <Avatar
                sx={{ mr: 2 }}
                alt="userProfile"
                src={collection.user.profile.photo.url}
              />
            ) : (
              <Avatar sx={{ bgcolor: "#2B2D6A", mr: 2 }}>
                <AccountCircleIcon />
              </Avatar>
            )}
            <Box>
              <Typography variant="body2" color="text.secondary">
                Collection by{" "}
                <Link
                  to={`/users/${collection.user.handle}`}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  @{collection.user.handle}
                </Link>
              </Typography>
            </Box>
            <Box sx={{ ml: "auto" }}>
              <Chip
                label={collection.public ? "Public" : "Private"}
                size="small"
                variant={collection.public ? "outlined" : "filled"}
                color={collection.public ? "primary" : "default"}
              />
            </Box>
          </Box>

          <Typography variant="h3" gutterBottom>
            {collection.title}
          </Typography>

          {collection.description && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {collection.description}
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary">
            {collection.itemsCount} items
          </Typography>
        </Box>

        {/* Collection Items - Tabbed Interface */}
        {sortedItems.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <Typography variant="h6" color={grey[400]}>
              This collection is empty
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: "100%", typography: "body1" }}>
            <TabContext value={tab}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <TabList onChange={handleTabChange}>
                  <Tab label={en.common.publications} value="1" />
                  <Tab label={en.common.posts} value="2" />
                </TabList>
              </Box>
              <TabPanel value="1" sx={{ px: 0 }}>
                {renderPublications(sortedItems)}
              </TabPanel>
              <TabPanel value="2" sx={{ px: 0 }}>
                {renderPosts(sortedItems)}
              </TabPanel>
            </TabContext>
          </Box>
        )}
      </Box>
    </React.Fragment>
  );
}

export default withRoot(Main);
